/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import BackButton from '../common/BackButton';
import StripePaymentModal from '../common/StripePaymentModal';
import './ExecutivePurchaseCredits.css';

const ExecutivePurchaseCredits: React.FC = () => {
  const { currentUser } = useAuth();
  const [currentCredits, setCurrentCredits] = useState<number>(0);
  const [currentCreditsFormatted, setCurrentCreditsFormatted] = useState<string>('$0.00');
  const [dollarAmount, setDollarAmount] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showStripePayment, setShowStripePayment] = useState<{
    costInCents: number;
    costFormatted: string;
  } | null>(null);

  useEffect(() => {
    fetchCurrentCredits();
  }, []);

  const fetchCurrentCredits = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user info which includes company credits
      const response = await apiClient.getCurrentUser();

      if (response.success && response.participant?.company) {
        const credits = response.participant.company.credits || 0;
        setCurrentCredits(credits);
        setCurrentCreditsFormatted(formatCentsAsDollars(credits));
      } else {
        setError('Failed to load credit information');
      }
    } catch (err: any) {
      console.error('Error fetching credits:', err);
      setError(err.message || 'Failed to load credit information');
    } finally {
      setLoading(false);
    }
  };

  const formatCentsAsDollars = (cents: number): string => {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(dollars);
  };

  const handleDollarAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setDollarAmount(value);
    }
  };

  const handlePurchaseClick = () => {
    const amount = parseFloat(dollarAmount);

    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount greater than $0.00');
      return;
    }

    if (amount < 0.50) {
      alert('Minimum purchase amount is $0.50');
      return;
    }

    if (amount > 10000) {
      alert('Maximum purchase amount is $10,000.00');
      return;
    }

    const costInCents = Math.round(amount * 100);
    const costFormatted = formatCentsAsDollars(costInCents);

    setShowStripePayment({
      costInCents,
      costFormatted
    });
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const response = await apiClient.confirmPayment(paymentIntentId);

      if (response.success) {
        alert(`Payment successful! ${response.newCreditBalanceFormatted} has been added to your account.`);

        // Update current credits display
        if (response.newCreditBalance !== undefined) {
          setCurrentCredits(response.newCreditBalance);
        }
        if (response.newCreditBalanceFormatted) {
          setCurrentCreditsFormatted(response.newCreditBalanceFormatted);
        }

        // Clear the input
        setDollarAmount('');
        setShowStripePayment(null);
      } else {
        alert('Payment confirmation failed. Please contact support.');
        setShowStripePayment(null);
      }
    } catch (err: any) {
      console.error('Error confirming payment:', err);
      alert(`Error confirming payment: ${err.message}`);
      setShowStripePayment(null);
    }
  };

  const handleStripePaymentCancel = () => {
    setShowStripePayment(null);
  };

  if (loading) {
    return (
      <div className="purchase-credits-container">
        <div className="loading">Loading credit information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="purchase-credits-container">
        <BackButton to="/" />
        <div className="error-message">
          <h3>Error Loading Credits</h3>
          <p>{error}</p>
          <button onClick={fetchCurrentCredits} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-credits-container">
      <BackButton to="/" />

      <div className="purchase-credits-header">
        <h2>Purchase Credits</h2>
      </div>

      <div className="credits-card">
        <div className="current-balance">
          <h3>Current Credit Balance</h3>
          <div className="balance-amount">{currentCreditsFormatted}</div>
        </div>

        <div className="purchase-form">
          <h3>Add Credits</h3>

          <div className="form-group">
            <label htmlFor="dollar-amount">Amount to Purchase (USD)</label>
            <div className="dollar-input-wrapper">
              <span className="dollar-sign">$</span>
              <input
                id="dollar-amount"
                type="text"
                value={dollarAmount}
                onChange={handleDollarAmountChange}
                placeholder="0.00"
                className="dollar-input"
              />
            </div>
            <small className="input-hint">Minimum: $0.50 | Maximum: $10,000.00</small>
          </div>

          <button
            className="purchase-button"
            onClick={handlePurchaseClick}
            disabled={!dollarAmount || parseFloat(dollarAmount) <= 0}
          >
            Purchase Credits
          </button>
        </div>

        <div className="info-section">
          <h4>How It Works</h4>
          <ul>
            <li>Enter the dollar amount you want to add to your credit balance</li>
            <li>Click "Purchase Credits" to proceed to secure payment</li>
            <li>Credits will be immediately added to your account after successful payment</li>
            <li>Use credits to generate and update bid packages</li>
          </ul>
        </div>

        {currentUser?.company && (
          <div className="cost-info">
            <h4>Bid Package Costs</h4>
            <table className="cost-table">
              <tbody>
                <tr>
                  <td>Generate New Bid Package:</td>
                  <td className="cost-value">
                    {formatCentsAsDollars(currentUser.company.generateBidCost)}
                  </td>
                </tr>
                <tr>
                  <td>Update Existing Bid Package:</td>
                  <td className="cost-value">
                    {formatCentsAsDollars(currentUser.company.updateBidCost)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showStripePayment && (
        <StripePaymentModal
          costInCents={showStripePayment.costInCents}
          costFormatted={showStripePayment.costFormatted}
          onSuccess={handleStripePaymentSuccess}
          onCancel={handleStripePaymentCancel}
        />
      )}
    </div>
  );
};

export default ExecutivePurchaseCredits;
